import { MenuDivider, MenuItem } from "@blueprintjs/core";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router";

import { CertificationParticipants } from "@/components/pages_logged_in/certifications/CertificationParticipants";
import { EditCertificationDrawer } from "@/components/pages_logged_in/certifications/EditCertificationDrawer";
import { useCertificationDetails } from "@/components/pages_logged_in/certifications/useCertificationDetails";
import { ExpiringIcon } from "@/components/pieces/icons/svg/ExpiringIcon";
import { SuccessIcon } from "@/components/pieces/icons/svg/SuccessIcon";
import { MoreMenu } from "@/components/pieces/more/MoreMenu";
import { Alert, AlertClose } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CertificationDetailsQuery } from "@/graphql";

function CertificationDetailsHeader({
  name,
  onEdit,
  onDelete,
}: {
  name: string | undefined;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex bg-background py-6 px-9 border-b justify-center">
      <div className="flex w-full max-w-content justify-between items-end">
        <div className="flex flex-col gap-4">
          <Link className="flex items-center text-sm" to="/certifications">
            <ChevronLeft className="w-4 h-4" strokeWidth={3} />
            View Certifications
          </Link>
          <div className="text-2xl font-semibold">{name}</div>
        </div>
        <div className="flex items-center gap-4 ml-auto">
          <MoreMenu>
            <MenuItem text="Edit Certification" onClick={onEdit} />
            <MenuDivider />
            <MenuItem
              text="Delete Certification"
              intent="danger"
              onClick={onDelete}
            />
          </MoreMenu>
        </div>
      </div>
    </div>
  );
}

function CertificationSummary({
  certification,
}: {
  certification: CertificationDetailsQuery["certification"];
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-4 w-full">
      <Card variant="green" className="flex-content">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SuccessIcon />
            Certified
          </CardTitle>
        </CardHeader>
        <CardContent>{certification?.participants?.totalActive}</CardContent>
      </Card>
      <Card variant="yellow" className="flex-content">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-nowrap ">
            <ExpiringIcon /> Expiring next 30 days
          </CardTitle>
        </CardHeader>
        <CardContent>
          {certification?.participants?.totalExpiringSoon}
        </CardContent>
      </Card>
      <Card variant="red" className="flex-content">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExpiringIcon /> Expired
          </CardTitle>
        </CardHeader>
        <CardContent>{certification?.participants?.totalExpired}</CardContent>
      </Card>
    </div>
  );
}

export function CertificationDetails() {
  const {
    data,
    error,
    isEditOpen,
    handleEdit,
    handleOpenChange,
    handleIssueManually,
    handleRevoke,
    handleDelete,
    handleCloseError,
    availableGroups,
    initialExpiration,
  } = useCertificationDetails();

  return (
    <div className="md:ml-sidebar flex flex-col h-full">
      <div className="flex flex-col sm:h-full sm:overflow-y-auto">
        <CertificationDetailsHeader
          name={data?.certification?.name}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
        <div className="flex flex-col gap-4 max-w-content m-4 w-full self-center">
          {error && (
            <Alert variant="destructive">
              <AlertClose onClick={handleCloseError} />
              {error}
            </Alert>
          )}
          <Card className="py-8 w-full">
            <CardHeader className="px-8 gap-6">
              <CardTitle>Certified Members</CardTitle>
              <CertificationSummary certification={data?.certification} />
            </CardHeader>
            <CardContent className="px-8 gap-6">
              <CertificationParticipants
                availableGroups={availableGroups}
                onIssueManually={handleIssueManually}
                onRevoke={handleRevoke}
                workspaceId={data?.currentWorkspace?.id}
              />
            </CardContent>
          </Card>
        </div>
      </div>
      <EditCertificationDrawer
        key={`edit-${data?.certification?.id}-${Boolean(isEditOpen)}`}
        certificationId={data?.certification?.id ?? ""}
        initialName={data?.certification?.name ?? ""}
        initialDescription={data?.certification?.description ?? ""}
        initialExpiration={initialExpiration}
        workspaceName={data?.currentWorkspace?.name ?? ""}
        workspaceLogo={data?.currentWorkspace?.logo ?? ""}
        isOpen={Boolean(isEditOpen)}
        onOpenChange={handleOpenChange}
      />
    </div>
  );
}
